'use client';

import React, { useDeferredValue, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { AnimatePresence, m } from 'motion/react';
import { css, cx } from 'styled-system/css';
import { card, chip, input } from 'styled-system/recipes';

import { Lift, Reveal, useDesignMotion } from '@shaojiang/components/motion';
import ShaojiangDate from '@shaojiang/components/ShaojiangDate';
import { fill, type SiteCopy } from '@shaojiang/content';
import { ARTICLE_TOPICS, type ArticleSummary } from '@shaojiang/data/articles';
import { DesignOnly } from '@shaojiang/designs/runtime/DesignOnly';
import type { Locale } from '@shaojiang/types/i18n';

import { ArrowTag } from './ArrowTag';
import { ArticleCard } from './ArticleCard';
import { CoverPlate } from './CoverPlate';
import { FilterChips } from './FilterChips';
import { grid3 } from './layout';

interface ArticlesBrowserProps {
  articles: ArticleSummary[];
  copy: SiteCopy['articles'];
  minutes: string;
  locale: Locale;
}

/** Search box (Nebula shows it beside the title). */
export function ArticleSearch({
  value,
  onChange,
  copy,
}: {
  value: string;
  onChange: (v: string) => void;
  copy: SiteCopy['articles'];
}) {
  return (
    <label
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5',
        w: 'min(22rem, 100%)',
      })}
    >
      <span className={css({ textStyle: 'xs', color: 'fg.muted' })}>{copy.search}</span>
      <span className={css({ position: 'relative' })}>
        <Search
          className={css({
            position: 'absolute',
            left: '3',
            top: '50%',
            translate: '0 -50%',
            w: '4',
            h: '4',
            color: 'fg.subtle',
          })}
        />
        <input
          type='search'
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={copy.searchPlaceholder}
          className={cx(input(), css({ pl: '9', w: 'full', h: '11' }))}
        />
      </span>
    </label>
  );
}

/**
 * Article list: topic chips (and search), a featured article in Nebula, then the grid.
 * Cards re-flow smoothly as the filter changes.
 */
export function ArticlesBrowser({ articles, copy, minutes, locale }: ArticlesBrowserProps) {
  const [topic, setTopic] = useState('all');
  const [query, setQuery] = useState('');
  const search = useDeferredValue(query.trim().toLowerCase());
  const motion = useDesignMotion();

  const topics = ARTICLE_TOPICS.filter((t) => articles.some((a) => a.topic === t));
  const filtered = useMemo(
    () =>
      articles.filter(
        (a) =>
          (topic === 'all' || a.topic === topic) &&
          (!search || `${a.title} ${a.excerpt} ${a.topic}`.toLowerCase().includes(search)),
      ),
    [articles, topic, search],
  );
  const [featured, ...rest] = filtered;
  const browsing = topic !== 'all' || !!search;

  const item = {
    layout: true,
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.96 },
    transition: motion.layout,
  } as const;

  const grid = (list: ArticleSummary[], offset: number) => (
    <div className={grid3}>
      <AnimatePresence initial={false} mode='popLayout'>
        {list.map((article, i) => (
          <m.div key={article.slug} {...item}>
            <ArticleCard article={article} locale={locale} index={i + offset} />
          </m.div>
        ))}
      </AnimatePresence>
    </div>
  );

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: '9',
        _nebula: { gap: '10' },
      })}
    >
      <div
        className={css({
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          gap: '4',
          flexWrap: 'wrap',
        })}
      >
        <FilterChips
          id='topics'
          label={copy.filterLabel}
          options={[
            { value: 'all', label: copy.all },
            ...topics.map((t) => ({ value: t, label: t })),
          ]}
          value={topic}
          onChange={setTopic}
          className={css({ _storybook: { justifyContent: 'center', w: 'full' } })}
        />
        <DesignOnly design={['nebula', 'bento', 'studio']}>
          <ArticleSearch value={query} onChange={setQuery} copy={copy} />
        </DesignOnly>
      </div>

      {filtered.length === 0 && (
        <p className={css({ color: 'fg.muted', textAlign: 'center' })}>{copy.empty}</p>
      )}

      {/* Storybook: one grid. The others: the newest article gets a wide card first. */}
      <DesignOnly design='storybook'>{grid(filtered, 0)}</DesignOnly>
      <DesignOnly design={['nebula', 'bento', 'studio']}>
        {featured && !browsing && (
          <Reveal>
            <Lift
              href={`/articles/${featured.slug}`}
              className={cx(
                card().root,
                card({ interactive: true }).root,
                css({ flexDirection: 'row', flexWrap: 'wrap', alignItems: 'stretch' }),
              )}
            >
              <div className={css({ flex: '1 1 480px', p: '2.5' })}>
                <CoverPlate
                  glyph={featured.glyph}
                  plate={0}
                  className={css({ aspectRatio: '16 / 9', h: 'full' })}
                  glyphClassName={css({ textStyle: 'glyphMd' })}
                />
              </div>
              <div
                className={css({
                  flex: '1 1 360px',
                  p: '9',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  gap: '3.5',
                })}
              >
                <span
                  className={cx(
                    chip({ tone: 'accent', static: true }),
                    css({ alignSelf: 'flex-start' }),
                  )}
                >
                  {copy.featured} · {featured.topic}
                </span>
                <p className={css({ textStyle: 'h2' })}>{featured.title}</p>
                {featured.excerpt && (
                  <p className={css({ textStyle: 'body', color: 'fg.muted' })}>
                    {featured.excerpt}
                  </p>
                )}
                <p className={css({ textStyle: 'label', color: 'fg.subtle' })}>
                  <ShaojiangDate dateString={featured.date} locale={locale} withDay /> ·{' '}
                  {fill(minutes, { n: featured.minutes })}
                </p>
              </div>
              <ArrowTag />
            </Lift>
          </Reveal>
        )}
        {grid(browsing ? filtered : rest, browsing ? 0 : 1)}
      </DesignOnly>
    </div>
  );
}
